import { useEffect, useMemo, useState } from "react";
import { ScrollView, Text } from "react-native";
import { useTranslation } from "react-i18next";
import type {
  BodyFigure,
  GlucoseUnit,
  PlanStarter,
  Program,
  Sex,
} from "@heraion/shared";
import {
  athleteHasTargets,
  computeWeeklyTargets,
  isOfflineError,
  pickTodaySession,
} from "@heraion/shared";
import {
  HomeView,
  ProgramComposer,
  ProgramHighlightCarousel,
  QuickGlucoseCard,
  QuickWeightCard,
  resolveTheme,
  useCatalogItems,
  useHomeCardDrafts,
  usePlanGapsLauncher,
  useProgramsChangedOnUnmount,
  useSessionResults,
  useLocale,
  type PlanGapsLauncher,
} from "@heraion/ui";
import { apiFetch, ApiError } from "./api";
import { exerciseCatalog } from "./logger/exercisesCache";
import { confirmViaWindow } from "./confirm";
import { durableWrite } from "./durableWrite";
import { ExercisePickerMount } from "./ExercisePickerMount";
import { newPlanTarget, PlanComposerMount, type PlanEditorTarget } from "./PlanComposerMount";
import { HomeMeasurementsCard } from "./HomeMeasurementsCard";
import { useLoggerProfile } from "./logger/useLoggerProfile";
import { useGlucoseWeb } from "./useGlucoseWeb";
import { programCreate, type ComposerRequest, type ProgramCreate } from "./useCreateHubWeb";
import { useHomeSignals } from "./useHomeSignals";
import { useHomeLayoutProps } from "./useHomeLayoutProps";
import { ProfileLoadState } from "./ProfileLoadState";
import {
  bodyweightOf,
  useProfileUnitHeldWhile,
  useProfileUnitOrNull,
  useShellProfile,
} from "./ThemeProfileBridge";
import { useAccountKey } from "./auth/useAccountKey";
import { useThemePalette } from "./theme";
import {
  selfSessionResultsDeps,
  useSelfProgressDepsFor,
} from "./progress/progressDeps";
import { useSelfAthleteTargets } from "./progress/useSelfAthleteTargets";
import { useSelfCoverageDeps } from "./progress/useSelfCoverageDeps";
import { useFollowedPrograms } from "./useFollowedPrograms";
import { useProgramEditorData } from "./useProgramEditorData";
import { useWorkoutStarter } from "./useWorkoutStarter";


// THE Home surface on web-rnw (HR-S4) — ONE composed scroll (the shared
// HomeView), the SAME tree mobile's Home tab mounts: hero/carousel →
// readiness → weekly stats → recent PRs → quick entries → badges
// placeholder. Program/plan browsing + management moved to the Create-hub
// (sidebar "Crea"); its intents land here as a composerRequest and open the
// shared scheduler-composer full-screen. Start/New workout open the lifted
// Logger (RW-18) via onStartWorkout — see useWorkoutStarter.ts.
export function PlanScreen({
  onOpenProgress,
  onOpenPainMap,
  onSetGoals,
  composerRequest,
  onComposerRequestHandled,
  onStartWorkout,
  onOpenCreateHub,
  callerSub,
  athleteRoster,
}: {
  // Host nav seam: the readiness badge + recent-PRs taps jump to the
  // Progress tab (now that it's lifted). AppShell owns the tab state.
  onOpenProgress?: () => void;
  // HR-11: the pain map card's tap opens Settings -> Body, the same seam
  // WorkoutsScreen's onOpenPainMap already reuses for its logger veto line.
  onOpenPainMap?: () => void;
  // #393: the meter's empty state opens Me -> Goals, as on mobile.
  onSetGoals?: () => void;
  // Create-hub seam (HR-S4w): the hub's actions/library land here as a
  // composer request; this surface opens the right composer and reports
  // back so the shell clears the request.
  composerRequest?: ComposerRequest | null;
  onComposerRequestHandled?: () => void;
  // Cross-tab seam (mirrors onOpenProgress/composerRequest): AppShell turns
  // a built PlanStarter (or, for a plain "New workout", no starter at all)
  // into a composerRequest on the Workouts tab + a tab switch. Optional only
  // for prop-shape symmetry with the rest of this file's host seams —
  // AppShell always supplies it.
  onStartWorkout?: (starter?: PlanStarter) => void;
  // #120: the blank Home's pointer to the library opens the Create hub, the
  // same seam the Workouts tab's empty state uses.
  onOpenCreateHub?: () => void;
  // PE-48 — the assignee panel's two host seams, straight through to both
  // ProgramComposer mounts below. AppShell's own useCoachStatus(user.sub)
  // read feeds these; this screen must NOT fetch /api/me/coach-edges itself
  // (CN-coach-edges-fetch).
  callerSub?: string;
  athleteRoster?: ReadonlyArray<string>;
}) {
  const { dark, family } = useThemePalette();
  const { t, i18n } = useTranslation();
  const lang = useLocale();
  const theme = resolveTheme(dark, family);
  const catalog = useCatalogItems({ catalog: exerciseCatalog });
  // The followed programs + their progress, through the follow gate (the
  // same hook the Create hub's «today» card reads).
  const { followGate, programsState } = useFollowedPrograms();
  const { programsChanged, setProgramStatus } = programsState;
  // #336: the editors' device drafts, per account on this browser.
  const draftScope = useAccountKey("editorDraft");
  const { doc, savePatch } = useShellProfile();
  const homeLayoutProps = useHomeLayoutProps(doc, savePatch);
  const { formBadge, weekly, prs, units, bodyweightKg, refreshProfile } =
    useHomeSignals();
  // #343: `units` above paints from a cache; every write here waits for this.
  const writeUnits = useProfileUnitOrNull();
  // MX-8b: Home's quick-entry drafts, lifted above HomeView's wide/narrow
  // split so a resize mid-type survives (see useHomeCardDrafts's header).
  const homeDrafts = useHomeCardDrafts();
  const glucoseUnit = (doc?.["glucoseUnit"] as GlucoseUnit | undefined) ?? "mmol";
  const { readings: glucoseReadings, create: createGlucose } = useGlucoseWeb();
  // The FULL exercise catalog (muscleContrib) + raw session results — the
  // SAME shared hook + shape the mobile ProgramEditorScreen injects for the
  // composer's read-only prescription projection. Cache-first; degrades to
  // nothing while null. The meter, the composer and the Home widgets read the
  // self cache Progress reads, so the Home fetches once (CN-self-results-cache).
  const { results: sessionResults, catalog: fullCatalog } =
    useSessionResults(selfSessionResultsDeps);
  // ME-3e: the athlete's own weekly targets, for the Home meter and the Home
  // widgets' Progress deps below. ⚠️ Mobile reads the SAME document through its
  // own hook, not this one (see the note in @heraion/ui's barrel): the repoint
  // is tracked, the sharing is not a fact yet.
  const { value: athleteTargets, status: athleteTargetsStatus } =
    useSelfAthleteTargets();

  const [editing, setEditing] = useState<Program | null>(null);
  // #336 slice 6: the program editor's progress marker and MAXes.
  const editorData = useProgramEditorData(editing?.id);
  // New program: its start (ProgramCreate), from the request.
  const [creating, setCreating] = useState<ProgramCreate | null>(null);
  useProgramsChangedOnUnmount(editing !== null || creating !== null);
  // The plan composer's target: a plan to edit, or a new one (#368: blank or
  // from a session). Each request replaces the last (PlanComposerMount).
  const [planTarget, setPlanTarget] = useState<PlanEditorTarget | null>(null);
  // #343: an open editor keeps the unit it opened with, past a re-read.
  const editorOpen = editing !== null || creating !== null || planTarget !== null;
  const editorUnits = useProfileUnitHeldWhile(editorOpen);
  // PE-39/K5: the prescribe editor's Logger profile seam. Reads the same
  // shell doc this screen already holds; the hook exists so the shape is
  // built once for both the Logger mount and this one.
  const loggerProfile = useLoggerProfile(editorOpen);
  const [mutationError, setMutationError] = useState<string | null>(null);

  // Consume the Create-hub's composer request (create = no item, edit =
  // item attached), then hand the token back so a later remount of this
  // surface doesn't re-open a composer the user already closed.
  useEffect(() => {
    if (!composerRequest) return;
    if (composerRequest.kind === "program") {
      // One editor at a time: a request replaces the open one.
      setEditing(composerRequest.program ?? null);
      setCreating(
        composerRequest.program
          ? null
          : programCreate({ model: composerRequest.model, seed: composerRequest.seed }),
      );
    } else if (composerRequest.kind === "plan") {
      setPlanTarget(
        composerRequest.plan
          ? { kind: "edit", plan: composerRequest.plan }
          : newPlanTarget(composerRequest.seed ?? null),
      );
    }
    // "workout" requests are consumed by WorkoutsScreen's own
    // composerRequest handling (RW-18) — nothing for the Plan surface to do.
    onComposerRequestHandled?.();
  }, [composerRequest, onComposerRequestHandled]);

  const resolveName = useMemo(() => {
    const bySlug = new Map(catalog.items.map((c) => [c.slug, c.name]));
    return (slug: string, snapshot?: string) =>
      bySlug.get(slug) ?? snapshot ?? slug;
  }, [catalog.items]);

  // #393: the star (set on the phone) leads the carousel and today's pick.
  const currentProgramId = doc?.["currentProgramId"] as string | undefined;
  const today = useMemo(
    () =>
      pickTodaySession(
        programsState.followedPrograms,
        programsState.progress,
        lang,
        currentProgramId,
      ),
    [programsState.followedPrograms, programsState.progress, lang, currentProgramId],
  );

  // One array per catalog load, for the starter and the carousel cards
  // (each memoizes its target coverage on it).
  const carouselCatalog = useMemo(
    () => (catalog.raw ? [...catalog.raw] : null),
    [catalog.raw],
  );

  // "Start" (today's session / a program-highlight card) → build a
  // PlanStarter and hand it to AppShell, which opens the Logger on the
  // Workouts tab pre-populated (mirrors mobile's identical useWorkoutStarter
  // wiring in apps/mobile/src/screens/PlanScreen.tsx).
  const { starting, startProgramSession, startBuiltSession } = useWorkoutStarter(
    carouselCatalog,
    // #343: the doc's, not the Home cache's: a start's loads wait for it.
    bodyweightOf(doc),
    (starter) => onStartWorkout?.(starter),
  );
  const heroLoading =
    programsState.programsStatus === "loading" &&
    programsState.programs.length === 0;
  const hasFollowed = programsState.followedPrograms.length > 0;

  // RW-17: the AP-3 weekly meter, scored on the athlete's own targets, as on
  // mobile. #393: no program feeds it; each program card says what it covers.
  const weeklyTargets = useMemo(
    () =>
      sessionResults === null || fullCatalog === null
        ? null
        : computeWeeklyTargets(
            sessionResults,
            fullCatalog,
            Date.now(),
            {},
            athleteTargets,
          ),
    [sessionResults, fullCatalog, athleteTargets],
  );
  // The "set your goals" signal, the same helper mobile reads.
  const hasAthleteTargets = athleteHasTargets(athleteTargetsStatus, athleteTargets);

  // HR-11 fetta 2: the Progress deps for the off-by-default Home widgets,
  // from the doc and targets read above. Building them fetches nothing; the
  // panels read the SAME session-results cache as Progress.
  const baseProgressDeps = useSelfProgressDepsFor(doc);
  const progressDeps = useSelfCoverageDeps(baseProgressDeps, {
    figure: doc?.["preferredFigure"] as BodyFigure | undefined,
    athleteTargets,
    startBuiltSession: onStartWorkout ? startBuiltSession : undefined,
  });
  // #492 F5: «plan the gaps» on a followed program's card opens today's draft.
  const onPlanGaps: PlanGapsLauncher | undefined = usePlanGapsLauncher(progressDeps);

  const surfaceError = (err: Error | null) =>
    setMutationError(
      err === null
        ? null
        : isOfflineError(err)
          ? // #397: words, not «0: api_offline».
            t("inbox.actionErrorOffline")
          : err instanceof ApiError
            ? `${err.status}: ${err.message}`
            : err.message,
    );

  const saveWeight = async (canonicalKg: number): Promise<boolean> => {
    try {
      await apiFetch("/api/me/measurements", {
        method: "POST",
        body: { takenAt: new Date().toISOString(), weightKg: canonicalKg },
      });
      await refreshProfile();
      return true;
    } catch {
      return false;
    }
  };

  if (editing || creating) {
    if (editorUnits === null || loggerProfile === null || !catalog.ready) return <ProfileLoadState theme={theme} />;
    return (
      <ProgramComposer
        key={editing?.id ?? creating?.nonce}
        // #336 slice 6: ProgramEditorV3, as on the phone. ⛔ Without profile
        // and confirm the composer returns null, a blank editor.
        mode="prescribe"
        profile={loggerProfile}
        apiFetch={apiFetch}
        durableWrite={durableWrite}
        dark={dark}
        program={editing ?? undefined}
        initialModel={creating?.model}
        creationSeed={creating?.seed}
        // #472: New program's way to the ready programs, as the Home's.
        onOpenLibrary={onOpenCreateHub}
        {...editorData}
        catalog={catalog.items}
        Picker={ExercisePickerMount}
        exerciseCatalog={fullCatalog ?? undefined}
        unitsPreference={editorUnits}
        confirm={confirmViaWindow}
        callerSub={callerSub}
        athleteRoster={athleteRoster}
        // #336: Save keeps the editor open; the list catches up on the way
        // out, which is the only way out now. A change notice (#380), so the
        // other programs lists see the saved program too.
        keepOpen
        draftScope={draftScope}
        draftOwner={callerSub ?? null}
        onSaved={() => {
          setEditing(null);
          setCreating(null);
          programsChanged();
        }}
        onCancel={() => {
          setEditing(null);
          setCreating(null);
          programsChanged();
        }}
      />
    );
  }

  if (planTarget) {
    if (editorUnits === null || !catalog.ready) return <ProfileLoadState theme={theme} />;
    return (
      <PlanComposerMount
        target={planTarget}
        dark={dark}
        catalog={catalog.items}
        units={editorUnits}
        callerSub={callerSub}
        athleteRoster={athleteRoster}
        draftScope={draftScope}
        onClose={() => setPlanTarget(null)}
      />
    );
  }

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.palette.bg }}
      showsVerticalScrollIndicator={false}
    >
      {mutationError ? (
        <Text
          style={{
            color: theme.palette.danger,
            fontSize: theme.typography.sizes.sm,
            marginHorizontal: 12,
            marginTop: 8,
          }}
        >
          {mutationError}
        </Text>
      ) : null}
      <HomeView
        theme={theme}
        today={today}
        resolveName={resolveName}
        heroLoading={heroLoading}
        isStartingToday={today !== null && starting === today.program.id}
        onStartToday={() => {
          if (today !== null) {
            void startProgramSession(today.program, today.weekIdx, today.sessionIdx);
          }
        }}
        onNewWorkout={() => onStartWorkout?.()}
        {...(onOpenCreateHub ? { onOpenLibrary: onOpenCreateHub } : {})}
        formBadge={formBadge}
        weekly={weekly}
        // RW-17 — wired to the same computeWeeklyTargets scorer mobile's Home
        // uses, off the composer's shared session-results fetch (above).
        weeklyTargets={weeklyTargets}
        hasAthleteTargets={hasAthleteTargets}
        onSetGoals={onSetGoals}
        onOpenProgress={onOpenProgress ?? (() => {})}
        prs={prs}
        units={units}
        locale={i18n.language}
        programHighlightSlot={
          hasFollowed ? (
            <ProgramHighlightCarousel
              followedPrograms={programsState.followedPrograms}
              progress={programsState.progress}
              catalog={carouselCatalog}
              resolveName={resolveName}
              unit={units}
              theme={theme}
              dark={dark}
              onStart={(program, weekIdx, sessionIdx) =>
                void startProgramSession(program, weekIdx, sessionIdx)
              }
              startingId={starting}
              onUnfollow={(prog) => {
                void setProgramStatus(prog.id, "archived").then(surfaceError);
              }}
              onEdit={setEditing}
              readOnlyProgramIds={followGate.followedProgramIds}
              onStopFollowing={(prog) => {
                void followGate.stopFollowing(prog).then(surfaceError);
              }}
              maxMeta={editorData.maxMeta}
              athleteTargets={athleteTargets}
              currentProgramId={currentProgramId}
              onPlanGaps={onPlanGaps}
            />
          ) : undefined
        }
        quickWeightSlot={
          writeUnits === null ? (
            <ProfileLoadState theme={theme} />
          ) : (
            <QuickWeightCard
              theme={theme}
              units={writeUnits}
              bodyweightKg={bodyweightKg}
              onSave={saveWeight}
              value={homeDrafts.draft("quickWeight")}
              onChangeText={(v) => homeDrafts.setDraft("quickWeight", v)}
            />
          )
        }
        quickGlucoseSlot={
          <QuickGlucoseCard
            theme={theme}
            unit={glucoseUnit}
            readings={glucoseReadings}
            nowMs={Date.now()}
            onSave={createGlucose}
            value={homeDrafts.draft("quickGlucose")}
            onChangeText={(v) => homeDrafts.setDraft("quickGlucose", v)}
          />
        }
        progressDeps={progressDeps}
        measurementsSlot={
          <HomeMeasurementsCard
            theme={theme}
            sex={doc?.["sex"] as Sex | undefined}
            units={units}
          />
        }
        onOpenPainMap={onOpenPainMap}
        {...homeLayoutProps}
      />
    </ScrollView>
  );
}
